import type { MessageSearchConstraints } from '@aurora/shared-generated/types/graphql-schema-types';
import type {
  ContextNodeFragment,
  ContextUserFragment
} from '@aurora/shared-generated/types/graphql-types';
import type { NodeType } from '@aurora/shared-types/nodes/enums';
import { EndUserQueryParams } from '@aurora/shared-types/pages/enums';
import type { ParsedUrlQuery } from '@aurora/shared-utils/helpers/urls/NextRoutes/Route';
import type { AuthUserFragment } from '../../types/graphql-types';
import {
  contentTypeMenuItemMap,
  lastUpdateMenuItemMap,
  repliesMenuItemMap
} from '../../helpers/search/SearchFilterMenuItemMapHelper';

/**
 * A Map of all the Search Widget Filters
 */
const searchWidgetFiltersMap: Record<string, object> = {
  lastUpdate: lastUpdateMenuItemMap,
  contentType: contentTypeMenuItemMap,
  replies: repliesMenuItemMap
};

/**
 * Map to get exact property Id that needs to be passed in payload.
 */
export const NodeTypeToIdMap: Record<NodeType, string> = {
  community: 'communityId',
  grouphub: 'groupHubId',
  category: 'categoryId',
  board: 'boardId'
};

/**
 * This function helps retrieve constraints that are used to fetch the message list depending
 * on all the filters that are enabled
 *
 * @author Akash Goenka
 *
 * @param query The query params
 * @param authUser The principal user
 * @param author The author(user) data
 * @param contextNode The context node
 * @param includeTagsFilterConstraint decides which query to add the tags filter constraint
 * @returns Object Of constraints for all the enabled filters
 */
function getSearchFilterParams(
  query: ParsedUrlQuery,
  authUser: AuthUserFragment,
  author?: ContextUserFragment,
  contextNode?: ContextNodeFragment,
  includeTagsFilterConstraint = true
): MessageSearchConstraints {
  let filterConstraints = {};
  Object.keys(query).forEach((key: EndUserQueryParams) => {
    const filterMenuItemMap = searchWidgetFiltersMap[key];
    if (key === EndUserQueryParams.SEARCH_FILTER_BY_AUTHOR && author) {
      filterConstraints = {
        ...filterConstraints,
        authorName: {
          eq: author.login
        }
      };
    }
    if (contextNode && key === EndUserQueryParams.SEARCH_FILTER_BY_LOCATION) {
      filterConstraints = {
        ...filterConstraints,
        [NodeTypeToIdMap[contextNode.nodeType]]: {
          eq: contextNode.id
        }
      };
    }
    if (key === EndUserQueryParams.SEARCH_ONLY_IN_TOPICS && query[key] === 'true') {
      filterConstraints = {
        ...filterConstraints,
        searchOnlyInRootPosts: {
          eq: true
        }
      };
    }
    if (key === EndUserQueryParams.SPELL_CHECK && query[key] === 'false') {
      filterConstraints = {
        ...filterConstraints,
        isSpellCheckEnabled: {
          eq: false
        }
      };
    }
    if (includeTagsFilterConstraint && key === EndUserQueryParams.SEARCH_BY_TAGS_FILTER) {
      filterConstraints = {
        ...filterConstraints,
        tagsFilter: {
          eq: query[key]
        }
      };
    }
    filterConstraints = {
      ...filterConstraints,
      ...(filterMenuItemMap ? filterMenuItemMap[query[key] as string] : {})
    };
  });
  return filterConstraints;
}

export { searchWidgetFiltersMap, getSearchFilterParams };
